import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { FiArrowLeft, FiArrowRight, FiDownload, FiInfo } from "react-icons/fi";
import PayoutBulkUploadFlowSteps from "./PayoutBulkUploadFlowSteps";
import PayoutDataTable from "./PayoutDataTable";
import { pickString, readSessionJson } from "./api/payoutMutationHelpers";

const defaultDraft = {
  fileName: "-",
  payoutAccount: null as null | {
    name?: string;
    bank?: string;
    accountNumber?: string;
  },
  rows: [] as Record<string, unknown>[],
};

const formatINR = (amount: number) =>
  `\u20B9${amount.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const displayText = (value?: string) => value?.trim() || "-";

const payoutAccountText = (account: typeof defaultDraft.payoutAccount) => {
  if (!account) return "-";
  const digits = String(account.accountNumber || "").replace(/\D/g, "");
  const last4 = digits ? digits.slice(-4) : String(account.accountNumber || "").slice(-4);
  return [displayText(account.name), account.bank, last4 ? `**** ${last4}` : ""]
    .filter((part) => part && part !== "-")
    .join(" | ") || "-";
};

function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between border-b border-[#E0E5F2] py-3 text-sm last:border-b-0">
      <span className="font-medium text-[#505E8E]">{label}</span>
      <span className="font-semibold text-[#1B163B]">{value}</span>
    </div>
  );
}

function PayoutBulkUploadReview() {
  const draft = useMemo(
    () => readSessionJson("payoutBulkTransferDraft", defaultDraft),
    [],
  );
  const draftRows = Array.isArray(draft.rows) ? draft.rows : [];
  const totalAmount = draftRows.reduce(
    (sum: number, row: Record<string, unknown>) =>
      sum + (Number(row.amount_paise) || Number(row.amountPaise) || 0) / 100,
    0,
  );
  const rows = draftRows.map((row) => [
    displayText(pickString(row, ["funding_account_name", "funding_account_id"])),
    displayText(pickString(row, ["beneficiary_name", "beneficiary_id"])),
    displayText(pickString(row, ["purpose"])),
    displayText(pickString(row, ["mode"])),
    formatINR((Number(row.amount_paise) || Number(row.amountPaise) || 0) / 100),
    <span className="rounded-[5px] bg-[#DCFCE7] px-2 py-1 text-xs font-semibold text-[#16A34A]">
      Ready
    </span>,
  ]);

  return (
    <div className="w-full px-4 pt-4 pb-8">
      <div className="mb-7">
        <h1 className="text-[22px] font-semibold leading-7 text-[#1B163B]">
          Review Bulk Upload
        </h1>
        <p className="mt-1 text-sm font-medium text-[#505E8E]">
          Review parsed transfers before verification.
        </p>
      </div>

      <PayoutBulkUploadFlowSteps activeStep={2} />

      <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_360px]">
        <section className="rounded-[8px] border border-[#E0E5F2] bg-white p-5 new-shadow">
          <div className="mb-5 flex flex-wrap items-center justify-between gap-4">
            <div>
              <h2 className="text-[18px] font-semibold text-[#1B163B]">
                Parsed Transfer Preview
              </h2>
              <p className="mt-1 text-sm font-medium text-[#505E8E]">
                {draftRows.length} rows parsed. Final validation runs before submit.
              </p>
            </div>
            <button className="flex h-10 items-center gap-3 rounded-[6px] border border-[#E0E5F2] bg-white px-4 text-sm font-semibold text-[#1B163B]">
              <FiDownload />
              Export validation report
            </button>
          </div>

          <PayoutDataTable
            minWidth="min-w-[900px]"
            columns={[
              "Payout Account",
              "Beneficiary",
              "Purpose",
              "Method",
              "Amount",
              "Validation",
            ]}
            rows={rows}
          />

          <div className="mt-5 flex gap-3 rounded-[6px] bg-[#1B163B0D] px-5 py-4 text-sm font-medium text-[#1B163B]">
            <FiInfo className="mt-0.5 shrink-0 text-lg" />
            Bulk transfers will be submitted together after Google Authenticator verification.
          </div>

          <div className="mt-6 flex items-center justify-between">
            <Link
              to="/payouts/transfers/bulk-upload"
              className="flex h-10 items-center gap-2 rounded-[6px] border border-[#E0E5F2] bg-white px-7 text-sm font-semibold text-[#1B163B]"
            >
              <FiArrowLeft />
              Back
            </Link>
            <Link
              to="/payouts/transfers/bulk-upload/verify"
              className="flex h-10 items-center gap-3 rounded-[6px] bg-[#1B163B] px-7 text-sm font-semibold text-white"
            >
              Continue to Verification
              <FiArrowRight />
            </Link>
          </div>
        </section>

        <aside className="h-fit rounded-[8px] border border-[#E0E5F2] bg-white p-6 new-shadow">
          <h2 className="text-[18px] font-semibold text-[#1B163B]">
            Upload Summary
          </h2>
          <div className="my-5 h-px bg-[#E0E5F2]" />
          <SummaryRow label="File Name" value={displayText(draft.fileName)} />
          <SummaryRow label="Payout Account" value={payoutAccountText(draft.payoutAccount)} />
          <SummaryRow label="Total Rows" value={String(draftRows.length)} />
          <SummaryRow label="Valid Rows" value={String(draftRows.length)} />
          <SummaryRow label="Rows with Errors" value="0" />
          <SummaryRow label="Total Amount" value={formatINR(totalAmount)} />
          <div className="mt-5 rounded-[6px] bg-[#DCFCE7] px-4 py-3 text-sm font-medium text-[#166534]">
            This file is ready to be submitted for verification.
          </div>
        </aside>
      </div>
    </div>
  );
}

export default PayoutBulkUploadReview;
